// Selection-only searchable dropdown. Type to filter, click an option
// to pick it. Distinct from Combobox — this one will NOT keep an
// arbitrary typed string as the value; the input falls back to the
// selected option's label when the popup closes.
//
// Useful when display ≠ value (e.g. countries: ISO code stored, full
// name shown).

import { For, Show, createMemo, createSignal, type Accessor } from "solid-js";
import { Portal } from "solid-js/web";
import { usePopupAnchor } from "../../shared/hooks/usePopupAnchor";
import s from "./SearchableSelect.module.css";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  value: Accessor<string>;
  setValue: (v: string) => void;
  options: Accessor<SelectOption[]>;
  placeholder?: string;
  // When set, prepends an option with value="" (e.g. "— All ports —")
  // representing "no selection".
  emptyLabel?: string;
  disabled?: Accessor<boolean>;
  id?: string;
}

export function SearchableSelect(props: SearchableSelectProps) {
  const [search, setSearch] = createSignal("");
  const anchor = usePopupAnchor();
  let inputEl: HTMLInputElement | undefined;

  const fullOptions = createMemo<SelectOption[]>(() => {
    const opts = [...props.options()];
    if (props.emptyLabel !== undefined) {
      opts.unshift({ value: "", label: props.emptyLabel });
    }
    return opts;
  });

  const selectedLabel = createMemo(() => {
    const v = props.value();
    const opt = fullOptions().find((o) => o.value === v);
    return opt?.label ?? v;
  });

  const filtered = createMemo(() => {
    const q = search().trim().toLowerCase();
    if (!q) return fullOptions();
    return fullOptions().filter((o) =>
      o.label.toLowerCase().includes(q) ||
      o.value.toLowerCase().includes(q),
    );
  });

  function openDropdown() {
    if (props.disabled?.()) return;
    setSearch("");
    anchor.show();
  }
  function closeDropdown() { anchor.hide(); }

  function pick(opt: SelectOption) {
    props.setValue(opt.value);
    setSearch("");
    closeDropdown();
    inputEl?.blur();
  }

  return (
    <>
      <input
        ref={(el) => { inputEl = el; anchor.bindTrigger(el); }}
        id={props.id}
        type="text"
        class={s.input}
        autocomplete="off"
        placeholder={props.placeholder}
        disabled={props.disabled?.()}
        // Open: show what the user is typing. Closed: show the
        // resolved label of the current value (or empty placeholder).
        value={anchor.open() ? search() : selectedLabel()}
        onFocus={openDropdown}
        onInput={(e) => {
          setSearch(e.currentTarget.value);
          if (!anchor.open()) openDropdown();
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            setSearch("");
            closeDropdown();
            inputEl?.blur();
          } else if (e.key === "Enter") {
            e.preventDefault();
            const f = filtered();
            // Single match wins; otherwise close without changing value.
            if (f.length === 1) pick(f[0]!);
            else closeDropdown();
          } else if (e.key === "ArrowDown" && !anchor.open()) {
            openDropdown();
          }
        }}
      />
      <Show when={anchor.open() && anchor.pos()}>
        <Portal>
          <div
            ref={(el) => anchor.bindPopup(el)}
            class={s.popup}
            style={{
              position: "fixed",
              top: `${anchor.pos()!.top}px`,
              left: `${anchor.pos()!.left}px`,
              width: `${anchor.pos()!.width}px`,
            }}
          >
            <Show when={filtered().length > 0} fallback={<div class={s.empty}>No matches</div>}>
              <For each={filtered()}>
                {(opt) => (
                  <div
                    class={`${s.option} ${opt.value === props.value() ? s.selected : ""}`}
                    onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
                  >
                    {opt.label}
                  </div>
                )}
              </For>
            </Show>
          </div>
        </Portal>
      </Show>
    </>
  );
}
